import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Voice capture.
 *
 * Deliberately one big button and a timer. No waveform, no scrubbing, no
 * pause/resume — the whole value of speaking instead of typing is that it
 * demands nothing, and a recorder with a control panel gives you something to
 * fiddle with instead of talking.
 *
 * Browser reality this has to absorb:
 * - Chrome and Firefox record `audio/webm;codecs=opus`; iOS Safari records
 *   `audio/mp4`. The supported type is probed rather than assumed.
 * - getUserMedia requires a secure context. localhost counts; a LAN IP over
 *   plain http does not, which is a confusing failure unless it's named.
 * - The microphone track must be stopped explicitly, or the browser keeps
 *   showing the recording indicator after you're done.
 */

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

/** Exported so the talk-through flow records in the same formats the API accepts. */
export function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
}

export interface Recording {
  blob: Blob;
  durationSeconds: number;
}

interface Props {
  onComplete: (recording: Recording) => void;
  disabled?: boolean;
}

export function VoiceRecorder({ onComplete, disabled }: Props): JSX.Element {
  const [state, setState] = useState<'idle' | 'recording' | 'finishing'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef(0);
  /*
   * THE SCREEN AS IT IS WHEN HE STOPS, not as it was when he started (22 Oct
   * 2026). `onstop` is wired up in `start`, and it called the `onComplete` of
   * the render that pressed record. Nick recorded from 11:59pm Wednesday to
   * 12:00am: the door's save was the 11:59 one, which saw nothing to file
   * under the night before, so it sent no day and the entry landed on
   * Thursday — the manual says a recording before 4am is saved under the
   * night it belongs to. Read through a ref, it is always the current one.
   */
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Release the microphone if this unmounts mid-recording, otherwise the
  // browser's recording indicator stays on after you navigate away.
  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  useEffect(() => {
    if (state !== 'recording') return;
    const timer = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    return () => clearInterval(timer);
  }, [state]);

  const start = useCallback(async () => {
    setError(null);

    if (!window.isSecureContext) {
      setError(
        "Your browser only allows microphone access over a secure connection. This works on localhost, but not over a plain http:// address on your network — you'd need HTTPS for that.",
      );
      return;
    }
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError("This browser can't record audio. You can type your entry instead.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const duration = (Date.now() - startedAtRef.current) / 1000;
        setState('idle');
        setSeconds(0);
        if (blob.size > 0) onCompleteRef.current({ blob, durationSeconds: duration });
      };

      startedAtRef.current = Date.now();
      // Timeslice so data arrives progressively — a long entry then survives a
      // crash or a navigation better than one buffered blob would.
      recorder.start(1000);
      recorderRef.current = recorder;
      setState('recording');
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      setError(
        name === 'NotAllowedError'
          ? 'Microphone access was blocked. You can allow it in your browser settings, or type your entry instead.'
          : name === 'NotFoundError'
            ? "No microphone found. You can type your entry instead."
            : "Couldn't start recording. You can type your entry instead.",
      );
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    setState('finishing');
    recorderRef.current?.stop();
    recorderRef.current = null;
  }, []);

  return (
    <div className="stack">
      {error && <div className="banner banner-caution">{error}</div>}

      {state === 'recording' ? (
        <>
          <button className="sos-button" onClick={stop} style={{ borderStyle: 'solid' }}>
            <strong>Stop</strong>
            <span aria-live="polite">{formatDuration(seconds)}</span>
          </button>
          <p className="faint" style={{ textAlign: 'center' }}>
            Say whatever's there. It doesn't need to be tidy or make sense.
          </p>
        </>
      ) : (
        <button
          className="btn btn-primary btn-lg btn-block"
          onClick={() => void start()}
          disabled={disabled || state === 'finishing'}
        >
          {state === 'finishing' ? <span className="spinner" /> : 'Start recording'}
        </button>
      )}
    </div>
  );
}

function formatDuration(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
